<template>
    <div class="fees-by-month w-full d-flex flex-column">
        <AonCol class="aon-col-12 px-0">
            <AonCard title="Upcoming Fees - By Month">
                <div class="inner-content">
                    <div id="feesMonthlyBarChart"></div>
                </div>
            </AonCard>
            <AonCoverLoading
                :loading="props.data.length === 0 && !props.loading"
                :show-spinner="false"
                :title="`No Fees By Month`"
                :message="`No fees were found for ${entityStore.entity?.name} between ${portfolioManagerStore.dateRange[0]} and ${portfolioManagerStore.dateRange[1]}`"
            />
        </AonCol>
    </div>
</template>

<script setup>
import { watch, computed, nextTick, onUnmounted } from 'vue'
import { useEntityStore, usePortfolioManagerStore } from '@/stores'

import * as am5 from '@amcharts/amcharts5'
import { useBarChart } from '@/composables/charts/barChart'
import { config } from '@/config'

import moment from 'moment'

const entityStore = useEntityStore()
const portfolioManagerStore = usePortfolioManagerStore()

let rootMonthly
let barChartMonthly

const emit = defineEmits(['item-clicked'])
const props = defineProps({
    data: {
        type: Array,
        default: () => [],
    },
    loading: {
        type: Boolean,
        default: false,
    },
})

watch(
    () => props.data,
    () => {
        initializeCharts()
    }
)

onUnmounted(() => {
    if (rootMonthly) rootMonthly.dispose()
})

const productDataByMonth = computed(() => {
    if (!props.data || props.data.length === 0) return []

    return props.data
        .map((entry) => {
            const date = new Date(entry.date)
            const dateInMs = date.getTime()

            // Calculate first day of the month
            const firstDay = new Date(date.getFullYear(), date.getMonth(), 1)
            const from = `${firstDay.getFullYear()}-${String(firstDay.getMonth() + 1).padStart(2, '0')}-${String(firstDay.getDate()).padStart(2, '0')} 00:00:00`

            // Calculate last day of the month
            const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0)
            const to = `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')} 00:00:00`

            return {
                date: dateInMs,
                name: moment(entry.date).format('MMM YYYY'), // This will be the x-axis category
                value: entry.upcomingFeeAmount,
                dateRange: {
                    from: from,
                    to: to,
                },
            }
        })
        .sort((a, b) => a.date - b.date)
})

const initializeCharts = async () => {
    // Initialize monthly chart
    if (rootMonthly) {
        rootMonthly.dispose()
    }

    await nextTick()

    am5.addLicense(config.license.AMChartsLicense)

    // Create monthly chart
    if (productDataByMonth.value.length > 0) {
        rootMonthly = am5.Root.new('feesMonthlyBarChart')
        barChartMonthly = useBarChart(rootMonthly)

        barChartMonthly.setClickCallback(handleBarClick)

        barChartMonthly.configureBar({ horizontal: false, scrollable: false })
        barChartMonthly.generateAxes(productDataByMonth.value, {
            showBullets: false,
            horizontal: false,
            formatCurrency: true,
            scrollable: false,
        })
        barChartMonthly.generateSeries(productDataByMonth.value, {
            horizontal: false,
            formatCurrency: true,
            clickable: true,
            altColor: true,
        })
    }
}

const handleBarClick = (barData) => {
    if (barData.date) {
        delete barData.name
        delete barData.originalData.name
    }
    emit('item-clicked', barData)
}
</script>

<style lang="scss" scoped>
.fees-by-month {
    width: 100%;
    position: relative;

    .card-content {
        width: 100%;
    }

    #feesMonthlyBarChart {
        width: 100%;
        min-height: 200px;

        div {
            height: 100%;
        }
    }

    #feesBarChart {
        width: 100%;
        min-height: 400px;

        div {
            height: 100%;
        }
    }
}
</style>
